import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Divider,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const AIScoresCard = ({ scores }) => {
  const theme = useTheme();

  const toValidNumberOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const n = typeof value === "number" ? value : Number(value);
    return Number.isFinite(n) ? n : null;
  };

  // API sometimes sends nulls or slightly different key names (e.g. behavioralScore)
  const behaviorScore = toValidNumberOrNull(
    scores?.behaviorScore ?? scores?.behaviourScore ?? scores?.behavioralScore,
  );
  const aiResponseScore = toValidNumberOrNull(
    scores?.aiResponse ?? scores?.answereScore ?? scores?.contentScore,
  );
  // Scores are on a 0–5 scale; normalize to percentage for the progress bars.
  const MAX_SCORE = 5;
  const behaviorPct =
    behaviorScore !== null
      ? Math.min(100, Math.max(0, (behaviorScore / MAX_SCORE) * 100))
      : 0;
  const responsePct =
    aiResponseScore !== null
      ? Math.min(100, Math.max(0, (aiResponseScore / MAX_SCORE) * 100))
      : 0;

  const behaviorFeedback =
    scores?.behaviorFeedback ??
    scores?.behavioralFeedback ??
    scores?.behavioralNotes ??
    null;

  const aiResponseFeedback =
    scores?.aiResponseFeedback ??
    scores?.contentFeedback ??
    null;

  return (
    <Card
      elevation={2}
      
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0px 1px 3px rgba(0, 0, 0, 0.3)"
            : "0px 1px 3px rgba(0, 0, 0, 0.1)",
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Typography
          variant="body1"
          sx={{ fontWeight: 600, mb: 3, color: theme.palette.text.primary }}
        >
          AI Scores
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  minWidth: 0,
                  overflowWrap: "anywhere",
                }}
              >
                AI Behavior Score
              </Typography>
              {behaviorFeedback && (
                <Tooltip
                  title={
                    <Typography variant="caption">
                      {behaviorFeedback}
                    </Typography>
                  }
                  arrow
                  placement="bottom"
                >
                  <IconButton
                    size="small"
                    color="primary"

                    sx={{
                      p: 0.25,
                      "&:hover": { color: theme.palette.text.primary },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: theme.palette.text.primary, flexShrink: 0 }}
            >
              {behaviorScore !== null
                ? Math.floor(behaviorScore * 10) / 10
                : "0"}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={behaviorPct}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "#e5e7eb",
              "& .MuiLinearProgress-bar": {
                backgroundColor: behaviorPct === 0 ? "transparent" : "#3b82f6",
                borderRadius: 4,
              },
            }}
          />
          {/* <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2, mb:0 }}
          >
          {scores.contentFeedback}
          </Typography> */}
        </Box>
        {/* <Divider /> */}
        <Box sx={{ mb:2}}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  minWidth: 0,
                  overflowWrap: "anywhere",
                }}
              >
                AI Response Score
              </Typography>
              {aiResponseFeedback && (
                <Tooltip
                  title={
                    <Typography variant="caption">
                      {aiResponseFeedback}
                    </Typography>
                  }
                  arrow
                  placement="bottom"
                >
                  <IconButton
                    size="small"
                    color="primary"
                    sx={{
                      p: 0.25,
                      //color: theme.palette.text.secondary,
                      "&:hover": { color: theme.palette.text.primary },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: theme.palette.text.primary, flexShrink: 0 }}
            >
              {aiResponseScore !== null
                ? Math.floor(aiResponseScore * 10) / 10
                : "0"}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={responsePct}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "#e5e7eb",
              "& .MuiLinearProgress-bar": {
                backgroundColor: responsePct === 0 ? "transparent" : "#10b981",
                borderRadius: 4,
              },
            }}
          />
        </Box>

       

        
        {/* <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 2, mb:0 }}
        >
        Feedback
        </Typography> */}
      </CardContent>
    </Card>
  );
};

export default AIScoresCard;
